import React, { useCallback, useRef } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { debounce } from '@material-ui/core';
import { FormApi } from 'final-form';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import Input, { Select, SelectItem } from 'ui/Input';
import { onlyNumbers } from 'utils/formatters';
import InputHint from '../InputHint/InputHint';
import { IFormValues, INetwork } from '../Beneficiaries.model';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

interface IAddBeneficiaryMobileMoney {
  values: IFormValues;
  form: FormType;
  networks: INetwork[];
  submitting: boolean;
  setValidatedAccountNo: (boolean) => void;
  nameHint: string;
  setNameHint: (value: string) => void;
  isLookingUp: boolean;
  setLookingUp: (value: boolean) => void;
}

const AddBeneficiaryMobileMoney = ({
  values,
  form,
  networks,
  submitting,
  setValidatedAccountNo,
  nameHint,
  setNameHint,
  isLookingUp,
  setLookingUp,
}: IAddBeneficiaryMobileMoney) => {
  const source = useRef<CancelTokenSource | null>(null);

  const lookupAccount = (phoneNumber: string, network: string, formInstance: FormType) => {
    if (source.current) {
      source.current.cancel();
    }
    source.current = axios.CancelToken.source();

    api
      .get(
        `transfer/mobilemoneylookup?phoneNumber=${phoneNumber}&network=${network}&transferType=A2W`,
        {},
        true,
        true,
        undefined,
        source.current?.token,
      )
      .then((response) => {
        if (response.accountName === null) {
          setNameHint('Account not found');
          setValidatedAccountNo(false);
          setLookingUp(false);
          formInstance.change('walletName', '');
        } else {
          setNameHint(response.accountName);
          setValidatedAccountNo(true);
          setLookingUp(false);
          formInstance.change('walletName', response.accountName);
        }
      })
      .catch((error: IApiResponse) => {
        setValidatedAccountNo(false);
        if (error.description) {
          console.error(error);
          setNameHint(error.description);
          setLookingUp(false);
          formInstance.mutators.changeFieldValue('walletName', '');
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeNetworkType = (formInstance: FormType, formValues: IFormValues) => ({ target }) => {
    formInstance.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()

    if (formValues.beneficiaryWalletNumber) {
      setLookingUp(true);
      setValidatedAccountNo(false);
      lookupAccount(formValues.beneficiaryWalletNumber, target.value as string, formInstance);
    }
  };

  const handleChangeWalletNumber = (formInstance: FormType) => ({ target }) => {
    const targetValue = target?.value.replace(/[^\d.]/g, '');
    formInstance.change(target?.name, targetValue);
    setLookingUp(true);
    setValidatedAccountNo(false);
    if (!targetValue) {
      setValidatedAccountNo(false);
      setNameHint('');
    }
    if (values.networkCode && targetValue) {
      debounced(targetValue as string, values?.networkCode, formInstance);
    }
  };
  return (
    <>
      <Input name="beneficiaryName" label="Full Name" disabled={submitting} />
      <Select
        name="networkCode"
        label="Network"
        disabled={submitting}
        SelectProps={{
          onChange: handleChangeNetworkType(form, values),
        }}
      >
        {networks.map(({ name, code }) => (
          <SelectItem key={code} value={code}>
            {name}
          </SelectItem>
        ))}
      </Select>
      <Input
        type="tel"
        name="beneficiaryWalletNumber"
        label="Beneficiary Wallet Number"
        onInput={handleChangeWalletNumber(form)}
        disabled={submitting}
        fieldProps={{
          format: (value: string) => (onlyNumbers(value)),
          parse: (value: string) => (onlyNumbers(value)),
        }}
      />
      {(nameHint || isLookingUp) && <InputHint hint={nameHint} loading={isLookingUp} onClose={() => setNameHint('')} />}
    </>
  );
};

export default AddBeneficiaryMobileMoney;
